import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { authClient, signOut } from "@/lib/auth";
import { api, tryRefreshSession } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import type { Session } from "@supabase/auth-js";
import { initPush, requestPushPermission, unregisterPush, syncPushSubscription } from "@/lib/push";
import { revenuecatLogIn, revenuecatLogOut } from "@/lib/revenuecat";
import { setSentryUser } from "@/lib/sentry";
import { markBoot, flushBootTiming } from "@/lib/bootTiming";
import { isSignoutSelfInitiated, logAuthEvent } from "@/lib/authDiag";

export interface User {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profileImageUrl?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionStatus?: string | null;
  xp: number;
  level: number;
  loginStreak: number;
  badges?: string[] | null;
  isAdmin: boolean;
  // Staff access tier ('user' | 'admin' | 'marketing' | 'support').
  // New source of truth for team roles (backend migration 101); isAdmin
  // is kept in lockstep with role === 'admin'. roleExpiresAt is an ISO
  // timestamp after which a scoped role lapses back to 'user' (NULL =
  // permanent). Backend returns snake_case; we read both shapes.
  role?: string | null;
  roleExpiresAt?: string | null;
  suspended?: boolean;
  reminderTime?: string | null;
  reminderEmail: boolean;
  weeklyDigestEnabled: boolean;
  visionCallbacksEnabled?: boolean;
  timezone?: string | null;
  coachTone?: string | null;
  referralCode?: string | null;
  referredBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  birth_year?: number | null;
  gender?: string | null;
  /** Server-computed list of required profile fields still missing, from
   *  `GET /api/users/me`. `undefined` means the payload predates the field —
   *  NOT that nothing is missing. See `profileGateDecision`. */
  profileMissingFields?: string[];
  xpGained?: number;
  leveledUp?: boolean;
  newLevel?: number;
  streakBonus?: boolean;
  pushEnabled?: boolean;
  leaderboardVisible?: boolean;
  onboardingComplete?: boolean;
  // Server-authoritative "First Session Guide done" flag (migration 065).
  // MainApp gates the first-run guide on this; normalizeUser MUST carry it or
  // the guide re-fires for brand-new users on a 2nd device / reinstall /
  // cleared storage (audit 2026-07-19).
  firstSessionComplete?: boolean;
  transformationVision?: string | null;
  // "Who I'm Becoming" identity layer — the chosen north star (user's own
  // words), complementing the earned identity stage (The Spark → Titan).
  becomingWho?: string | null;
  becomingWhy?: string | null;
  becomingValues?: string[] | null;
  // App-mode (Easy / Advanced / Custom nav). Restored 2026-06-13 after a
  // month of being bypassed (every user saw all 10 tabs). Default is now
  // 'advanced' (not the old 'easy') so reactivating modes never silently
  // strips tabs from an existing user — Easy / Custom are opt-in choices
  // made in Profile. Backend returns snake_case (`app_mode`/`custom_tabs`).
  appMode?: string | null;
  customTabs?: string[] | null;
  /** The cohort config this account arrived with, resolved server-side (T10).
   *  Null for the ordinary app. Its presence is also the "somebody chose this
   *  for him" signal the nav reads — see hooks/useAppMode.ts. */
  cohortConfig?: {
    name: string;
    label: string;
    blurb: string;
    faithContent: boolean;
    appMode: string;
    customTabs: string[];
  } | null;
  // Which food log wins on days that have both a TrybeUP log and an Apple
  // Health daily total: 'trybeup' (default) or 'apple_health'. Backend
  // returns snake_case (`nutrition_source`).
  nutritionSource?: string | null;
  // Identity Stages — snake_case since the backend returns the raw
  // SQLAlchemy column name. See apps/web/src/lib/identityStages.ts for
  // the full mapping of stage id → display name / tagline / icon.
  identity_stage?: string | null;
  stage_unlocked_at?: Record<string, string> | null;
  // Apple Guideline 5.1.1(i) / 5.1.2(i) — timestamp the user accepted
  // the in-app AI data-sharing consent dialog. NULL until accepted.
  // Backend returns snake_case (`ai_consent_at`); we read both shapes.
  ai_consent_at?: string | null;
  aiConsentAt?: string | null;
  /** Migration 227 — the user's own answer to "do you want the AI at all",
   *  separate from consent above. Consent NULL means "not asked yet, or
   *  revoked", and a surface may still ask. `aiEnabled === false` means they
   *  have answered and NO surface may ask again — hide it, never prompt.
   *  Read it through `isAiOff()` rather than off the raw field. */
  ai_enabled?: boolean | null;
  aiEnabled?: boolean | null;
  /** This account is the seeded demo, not a person (backend migration 223).
   *  Drives the banner that tells a visitor nothing he does here is kept —
   *  without it he'd be typing into a journal that gets wiped at 3am with no
   *  warning, which is the opposite of how this product talks to people. */
  isDemo?: boolean;
}

// ── Staff-role helpers (team-roles work) ────────────────────────────────
// Mirror the backend's User.effective_role / is_staff_admin so the UI gates
// match exactly what the API will allow. An expired scoped role reads as
// 'user'; the legacy isAdmin boolean always wins.

/** Expiry-aware role. A lapsed scoped role downgrades to 'user'. */
export function getEffectiveRole(user?: User | null): string {
  if (!user) return "user";
  if (user.isAdmin) return "admin";
  const role = user.role || "user";
  if (role === "user") return "user";
  if (user.roleExpiresAt) {
    const exp = new Date(user.roleExpiresAt).getTime();
    if (!Number.isNaN(exp) && exp < Date.now()) return "user";
  }
  return role;
}

/** Full root-equivalent access (founder / co-founder). */
export function isFullAdmin(user?: User | null): boolean {
  return getEffectiveRole(user) === "admin";
}

/** May open the Admin Dashboard at all (full admin OR scoped marketing). */
export function canAccessAdmin(user?: User | null): boolean {
  const r = getEffectiveRole(user);
  return r === "admin" || r === "marketing";
}

/**
 * A group leader, and ONLY a group leader (T17).
 *
 * Deliberately excludes admins and marketing: this is not "may see the leader
 * view", it is "this person is a leader and nothing else", which is what decides
 * whether `/admin` renders the whole back office or the single My Group screen.
 *
 * Why that swap rather than filtering the admin nav: AdminDashboard fires 23
 * queries on mount and only 7 are gated behind the admin check, so a leader
 * inside that shell would have his browser 403 against a dozen back-office
 * endpoints on every open. Swapping the route at the door means those queries
 * never mount, and a `?tab=users` deep link renders the leader screen anyway —
 * which fails closed harder than any tab list could.
 */
export function isLeaderOnly(user?: User | null): boolean {
  return getEffectiveRole(user) === "leader";
}

/** May open the leader view: a group leader, or a full admin previewing it. */
export function canAccessLeaderView(user?: User | null): boolean {
  const r = getEffectiveRole(user);
  return r === "leader" || r === "admin";
}

export function isPremiumUser(user: User | undefined | null): boolean {
  // Match the canonical server-side premium definition (backend user.py
  // is_premium): active OR past_due (Stripe dunning grace) OR promotional
  // access OR admin. A prior version only checked "active", which would
  // wrongly lock out a grace-period/promo/admin user if this helper were
  // wired into a gate. (Currently unused, but kept correct to defuse the trap.)
  if (!user) return false;
  const status = user.subscriptionStatus;
  return (
    status === "active" ||
    status === "past_due" ||
    Boolean((user as { promotionalAccess?: boolean }).promotionalAccess) ||
    Boolean((user as { isAdmin?: boolean }).isAdmin)
  );
}

function normalizeUser(raw: Record<string, unknown>): User {
  return {
    id: raw.id as string,
    email: (raw.email as string) ?? null,
    firstName: (raw.firstName ?? raw.first_name ?? null) as string | null,
    lastName: (raw.lastName ?? raw.last_name ?? null) as string | null,
    profileImageUrl: (raw.profileImageUrl ?? raw.profile_image_url ?? null) as string | null,
    stripeCustomerId: (raw.stripeCustomerId ?? raw.stripe_customer_id ?? null) as string | null,
    stripeSubscriptionId: (raw.stripeSubscriptionId ?? raw.stripe_subscription_id ?? null) as string | null,
    subscriptionStatus: (raw.subscriptionStatus ?? raw.subscription_status ?? null) as string | null,
    xp: (raw.xp as number) ?? 0,
    level: (raw.level as number) ?? 1,
    loginStreak: ((raw.loginStreak ?? raw.login_streak) as number) ?? 0,
    badges: (raw.badges as string[]) ?? null,
    isAdmin: ((raw.isAdmin ?? raw.is_admin) as boolean) ?? false,
    role: ((raw.role ?? null) as string | null),
    roleExpiresAt: ((raw.roleExpiresAt ?? raw.role_expires_at ?? null) as string | null),
    suspended: (raw.suspended as boolean) ?? false,
    reminderTime: (raw.reminderTime ?? raw.reminder_time ?? null) as string | null,
    reminderEmail: ((raw.reminderEmail ?? raw.reminder_email) as boolean) ?? false,
    weeklyDigestEnabled: ((raw.weeklyDigestEnabled ?? raw.weekly_digest_enabled) as boolean) ?? false,
    visionCallbacksEnabled: ((raw.visionCallbacksEnabled ?? raw.vision_callbacks_enabled) as boolean) ?? true,
    timezone: (raw.timezone as string) ?? null,
    coachTone: (raw.coachTone ?? raw.coach_tone ?? null) as string | null,
    referralCode: (raw.referralCode ?? raw.referral_code ?? null) as string | null,
    referredBy: (raw.referredBy ?? raw.referred_by ?? null) as string | null,
    createdAt: (raw.createdAt ?? raw.created_at ?? null) as string | null,
    updatedAt: (raw.updatedAt ?? raw.updated_at ?? null) as string | null,
    birth_year: (raw.birth_year as number) ?? null,
    // The server's own verdict on the required-profile rule. `undefined` when
    // the payload predates it (older bundle, or a cache entry restored from
    // before the field existed) — which is meaningfully different from `[]`,
    // so it must not be coerced to an empty array here. See
    // `profileGateDecision`.
    profileMissingFields: (raw.profileMissingFields as string[]) ?? undefined,
    gender: (raw.gender as string) ?? null,
    xpGained: (raw.xpGained ?? raw.xp_gained) as number | undefined,
    leveledUp: (raw.leveledUp ?? raw.leveled_up) as boolean | undefined,
    newLevel: (raw.newLevel ?? raw.new_level) as number | undefined,
    streakBonus: (raw.streakBonus ?? raw.streak_bonus) as boolean | undefined,
    pushEnabled: ((raw.pushEnabled ?? raw.push_enabled) as boolean) ?? false,
    leaderboardVisible: ((raw.leaderboardVisible ?? raw.leaderboard_visible) as boolean) ?? true,
    onboardingComplete: ((raw.onboardingComplete ?? raw.onboarding_complete) as boolean) ?? false,
    firstSessionComplete: ((raw.firstSessionComplete ?? raw.first_session_complete) as boolean) ?? false,
    transformationVision: (raw.transformationVision ?? raw.transformation_vision ?? null) as string | null,
    becomingWho: (raw.becomingWho ?? raw.becoming_who ?? null) as string | null,
    becomingWhy: (raw.becomingWhy ?? raw.becoming_why ?? null) as string | null,
    becomingValues: (raw.becomingValues ?? raw.becoming_values ?? null) as string[] | null,
    // Default 'advanced' (was 'easy' historically) — see the User-type note
    // above. A user with an explicit app_mode in the DB still gets it back.
    appMode: (raw.appMode ?? raw.app_mode ?? "advanced") as string | null,
    customTabs: (raw.customTabs ?? raw.custom_tabs ?? null) as string[] | null,
    // Sent already resolved by /users/me — the switches, not the column. There
    // is deliberately no second copy of the config table in the frontend.
    cohortConfig: (raw.cohortConfig ?? null) as User["cohortConfig"],
    nutritionSource: (raw.nutritionSource ?? raw.nutrition_source ?? "trybeup") as string | null,
    ai_consent_at: (raw.ai_consent_at ?? raw.aiConsentAt ?? null) as string | null,
    aiConsentAt: (raw.aiConsentAt ?? raw.ai_consent_at ?? null) as string | null,
    // Absent (an account from before migration 227, or a payload that predates
    // it) reads as ON — the app as shipped. Only an explicit false is "off".
    ai_enabled: (raw.ai_enabled ?? raw.aiEnabled ?? true) as boolean,
    aiEnabled: (raw.aiEnabled ?? raw.ai_enabled ?? true) as boolean,
    isDemo: ((raw.isDemo ?? raw.is_demo) as boolean) ?? false,
  };
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const hasShownGamification = useRef(false);

  useEffect(() => {
    // If this page load is an OAuth callback (Google etc.), the URL carries
    // a `?code=` or a hash `access_token=` that GoTrue's `detectSessionInUrl`
    // exchanges for a session during `getSession()` and emits via
    // `onAuthStateChange`. We must NOT short-circuit below in that case —
    // doing so leaves the code un-exchanged and bounces the user back to
    // the signup screen in a loop (the "sign up with Google loops" bug).
    const url = new URL(window.location.href);
    const isOAuthCallback =
      url.searchParams.has("code") ||
      url.hash.includes("access_token=") ||
      url.hash.includes("error=");

    // "Remember me" support: if the user unchecked "Remember me", we stored a
    // flag in localStorage. We also track a "last active" timestamp in
    // localStorage that this tab refreshes every 30s while visible. On load,
    // if the flag is set and the last-active timestamp is older than the idle
    // limit, treat it as a browser-close and sign out.
    //
    // Why localStorage (not sessionStorage): iOS Safari aggressively kills
    // backgrounded tabs when the phone sleeps, which wipes sessionStorage but
    // leaves localStorage intact. A sessionStorage tab marker therefore can't
    // distinguish "browser closed" from "phone slept and Safari reclaimed the
    // tab", and used to log Safari users out every time they unlocked their
    // phone. The timestamp survives tab suspension because it lives in
    // localStorage, but still expires after the idle limit when the browser
    // is genuinely closed for that long.
    //
    // Critical: the flag is ONLY meaningful for email/password sessions —
    // LoginPage is the only place it gets set, and only after a successful
    // signInWithPassword. A stale flag from a past password login must
    // never sign out a current Google session. Earlier versions of this
    // code required `provider !== "email"` to clear the flag, but if a
    // refreshed Google session came back from GoTrue with `app_metadata.provider`
    // undefined (rare, but it happens with linked identities and certain
    // refresh-token paths), the guard fell through and the user got signed
    // out. The rule is now inverted: enforce the idle-limit sign-out ONLY
    // when we can positively confirm `provider === "email"`. In every other
    // case (no session, OAuth session, missing provider) we treat the flag
    // as stale and clear it.
    const TEMP_SESSION_IDLE_LIMIT_MS = 30 * 60 * 1000;
    // Native app = personal device: the "sign out when the browser closes"
    // idea doesn't apply, and a 30-min idle sign-out just logs people out
    // mid-workout (founder incident 2026-08-07 — logged-out screen with a
    // workout still running). On native we treat the session as always
    // remembered: clear any temp flag so it can't fire, and never enforce
    // the idle timeout below. Web keeps the shared-computer behavior.
    const isNativeApp = Capacitor.isNativePlatform();
    if (isNativeApp) {
      try {
        localStorage.removeItem("trybeup_session_temporary");
        localStorage.removeItem("trybeup_last_active");
      } catch { /* storage blocked — nothing to clear */ }
    }
    const isTemporary =
      !isNativeApp && localStorage.getItem("trybeup_session_temporary") === "true";
    const lastActiveRaw = localStorage.getItem("trybeup_last_active");
    const lastActive = lastActiveRaw ? parseInt(lastActiveRaw, 10) : NaN;
    const idleMs = Number.isFinite(lastActive) ? Date.now() - lastActive : -1;
    const idleTooLong = idleMs > TEMP_SESSION_IDLE_LIMIT_MS;

    // Initialise OneSignal on native platforms. No-op on web.
    initPush().catch(() => {});

    // Helper to capture sign-in diagnostics. Written to sessionStorage
    // (read by LoginPage on mount) so the user sees the actual reason
    // an OAuth attempt bounced back to /login instead of a silent
    // bounce. Per-tab + auto-clears on browser close (sessionStorage).
    const writeSigninDiag = (
      source: 'oauth_redirect' | 'session_exchange' | 'native_exchange',
      detail: { code?: string; message?: string; url_excerpt?: string },
    ) => {
      try {
        sessionStorage.setItem(
          'trybeup_signin_diag',
          JSON.stringify({
            source,
            code: detail.code,
            message: detail.message,
            url_excerpt: detail.url_excerpt,
            at: new Date().toISOString(),
          }),
        );
      } catch { /* ignore — diagnostic only */ }
    };

    authClient.getSession().then(async ({ data }) => {
      let sess = data.session;
      // OAuth-callback recovery + diagnostics. If we landed here because of
      // `?code=` or `#access_token=` but getSession() resolved to no
      // session, the SDK's automatic detectSessionInUrl path silently
      // failed. Try a manual recovery before falling through to the
      // diagnostic banner — auth-js's `_getSessionFromURL` throws on any
      // missing field (refresh_token, expires_in, token_type) and the
      // throw is swallowed inside `_initialize`, but `setSession()` only
      // needs access_token + refresh_token and re-derives the rest.
      if (isOAuthCallback && !sess) {
        const hashStr = window.location.hash.startsWith('#')
          ? window.location.hash.slice(1)
          : window.location.hash;
        const hashParams = new URLSearchParams(hashStr);
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hashError = hashParams.get('error_description') || hashParams.get('error');
        const queryCode = url.searchParams.get('code');
        const flow: 'implicit' | 'pkce' | 'error' | 'unknown' = hashError
          ? 'error'
          : accessToken
            ? 'implicit'
            : queryCode
              ? 'pkce'
              : 'unknown';

        // Don't try to "recover" if the provider returned an explicit
        // error — that's already a definitive failure; surfacing it
        // truthfully is more useful than papering over it.
        if (flow === 'implicit' && accessToken && refreshToken) {
          try {
            const { data: setData, error: setErr } = await authClient.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (!setErr && setData.session) {
              // Strip the hash so a refresh doesn't loop through this
              // path again and so the tokens aren't sitting in the URL
              // bar / browser history.
              window.history.replaceState({}, '', url.pathname + url.search);
              sess = setData.session;
              logAuthEvent('oauth_recovery', { outcome: 'setSession-ok', flow });
            } else {
              logAuthEvent('oauth_recovery', {
                outcome: 'setSession-error',
                flow,
                message: setErr?.message ?? 'unknown',
              });
            }
          } catch (err) {
            logAuthEvent('oauth_recovery', {
              outcome: 'setSession-threw',
              flow,
              message: err instanceof Error ? err.message : String(err),
            });
          }
        } else if (flow === 'pkce' && queryCode) {
          try {
            const { data: exchData, error: exchErr } = await authClient.exchangeCodeForSession(queryCode);
            if (!exchErr && exchData.session) {
              url.searchParams.delete('code');
              window.history.replaceState(
                {},
                '',
                url.pathname + (url.search || '') + (url.hash || ''),
              );
              sess = exchData.session;
              logAuthEvent('oauth_recovery', { outcome: 'exchangeCode-ok', flow });
            } else {
              logAuthEvent('oauth_recovery', {
                outcome: 'exchangeCode-error',
                flow,
                message: exchErr?.message ?? 'unknown',
              });
            }
          } catch (err) {
            logAuthEvent('oauth_recovery', {
              outcome: 'exchangeCode-threw',
              flow,
              message: err instanceof Error ? err.message : String(err),
            });
          }
        }

        // If recovery didn't produce a session, write the diagnostic
        // for the LoginPage banner. Tailor the message to the actual
        // flow shape — the previous text blamed PKCE every time, which
        // was wrong whenever the URL was an implicit-flow response.
        if (!sess) {
          const present: string[] = [];
          const missing: string[] = [];
          for (const f of [
            'access_token',
            'refresh_token',
            'expires_in',
            'token_type',
            'expires_at',
            'provider_token',
          ] as const) {
            (hashParams.get(f) ? present : missing).push(f);
          }
          let message: string;
          if (flow === 'error') {
            message =
              `OAuth provider returned an error in the callback URL: ` +
              `${hashError ?? 'no description'}.`;
          } else if (flow === 'implicit') {
            message =
              `OAuth callback (implicit flow) returned tokens but the ` +
              `client couldn't create a session. Hash fields present: ` +
              `[${present.join(', ')}]. Missing: [${missing.join(', ')}]. ` +
              `Recovery attempt also failed — see oauth_recovery event ` +
              `in the auth log for the underlying reason.`;
          } else if (flow === 'pkce') {
            message =
              `OAuth callback (PKCE flow) had a ?code= but exchange ` +
              `failed. Most likely cause: the PKCE code_verifier stored ` +
              `at signInWithOAuth time is gone (different browser tab, ` +
              `cookies cleared, in-app browser sandbox). See ` +
              `oauth_recovery event in the auth log.`;
          } else {
            message =
              `OAuth callback URL had an unrecognised shape — no ?code=, ` +
              `no #access_token=, no #error=. URL excerpt below.`;
          }
          writeSigninDiag('session_exchange', {
            message,
            url_excerpt: window.location.search + window.location.hash,
          });
        }
      }

      const provider = sess?.user?.app_metadata?.provider;
      const isEmailSession = provider === "email";

      // Snapshot what we found in localStorage at mount-time. If a later
      // SIGNED_OUT fires unexpectedly the LoginPage diagnostic banner can
      // show whether the user even had a session to begin with.
      const expiresAt = sess?.expires_at;
      const secondsLeft = expiresAt ? expiresAt - Math.floor(Date.now() / 1000) : null;
      logAuthEvent('initial_session', {
        hasSession: !!sess,
        provider: provider ?? null,
        expiresAt: expiresAt ?? null,
        secondsLeft,
        isOAuthCallback,
        isTemporary,
        idleMs: idleMs >= 0 ? idleMs : null,
      });

      // Fast path: no flag set, nothing to enforce.
      if (!isTemporary) {
        setSession(sess);
        setSessionLoading(false);
        return;
      }

      // Stale-flag self-heal. The flag only makes sense for an active
      // email session — anything else (no session, Google, unknown
      // provider) is by definition stale. Clear and proceed without
      // touching the live session.
      if (!sess || !isEmailSession) {
        localStorage.removeItem("trybeup_session_temporary");
        localStorage.removeItem("trybeup_last_active");
        setSession(sess);
        setSessionLoading(false);
        return;
      }

      // Confirmed email session with the flag set. Apply the
      // "remember me OFF + browser idle too long" sign-out. Skip if
      // we're mid-OAuth-callback (defensive — shouldn't be reachable
      // with an email session, but cheap to guard).
      //
      // Route through the lib/auth.ts wrapper rather than calling
      // authClient.signOut() directly. The wrapper writes a
      // `signout_initiated` event with the reason AND marks the SIGNED_OUT
      // listener path as self-initiated, so this branch shows up in the
      // /login diagnostic as a legitimate idle-timeout sign-out instead
      // of an unexplained "ghost" SIGNED_OUT that runs through the
      // verify-session guard. Caught the gap on 2026-05-21 when the
      // founder's iPhone log was missing every cause-event because this
      // path wasn't instrumented.
      if (idleTooLong && !isOAuthCallback) {
        signOut('idle-temporary-session').catch(() => {});
        localStorage.removeItem("trybeup_session_temporary");
        localStorage.removeItem("trybeup_last_active");
        setSession(null);
        setSessionLoading(false);
        return;
      }

      // Email session within the idle window — refresh the timestamp so
      // refreshes and tab suspensions don't look like a browser-close.
      localStorage.setItem("trybeup_last_active", String(Date.now()));
      setSession(sess);
      setSessionLoading(false);
    }).catch((err) => {
      // getSession itself threw. If we were processing an OAuth
      // callback when this happened, capture the error so LoginPage
      // can surface it.
      if (isOAuthCallback) {
        const message = err instanceof Error ? err.message : String(err);
        writeSigninDiag('session_exchange', {
          message: `getSession() threw during OAuth callback: ${message}`,
          url_excerpt: window.location.search + window.location.hash,
        });
      }
      setSessionLoading(false);
    });

    const { data: listener } = authClient.onAuthStateChange(
      (event, newSession) => {
        // SIGNED_OUT path skips the eager setSession(newSession) below
        // so the ghost-sign-out guard can verify the session BEFORE
        // any React subscriber sees null. Without this, a downstream
        // route-guard could redirect to /login in the ~200ms gap
        // between the spurious event and the verify resolving.
        if (event !== "SIGNED_OUT") {
          setSession(newSession);
        }
        if (event === "PASSWORD_RECOVERY") {
          setIsPasswordRecovery(true);
        }
        if (event === "SIGNED_IN") {
          // Same rule as the load-time path — clear any stale temp-session
          // flag whenever the new session is anything other than a confirmed
          // email/password session. Conservative on purpose: a missing or
          // unrecognised `provider` must NOT leave the flag in place, or a
          // future browser-close → reopen will sign the user out.
          const provider = newSession?.user?.app_metadata?.provider;
          if (provider !== "email") {
            localStorage.removeItem("trybeup_session_temporary");
            localStorage.removeItem("trybeup_last_active");
          }
          // Ask the OS for push permission right after login.
          // No-op on web; on iOS shows the system permission dialog.
          requestPushPermission().catch(() => {});
          // Then reconcile the backend's stored subscription id with the
          // device's CURRENT one. Fires on every launch with a session, so a
          // stale/missing id (the cause of "automatic nudges never arrive but
          // the test says sent") self-heals here. See lib/push.ts.
          syncPushSubscription().catch(() => {});
          // Identify the user to RevenueCat so any subsequent IAP receipt
          // is filed against the correct account. Uses the GoTrue auth ID
          // (session.user.id) — backend webhook falls back to gotrue_id
          // lookup so the user row resolves either way.
          if (newSession?.user?.id) {
            revenuecatLogIn(newSession.user.id).catch(() => {});
          }
          // Tag every subsequent Sentry event with the signed-in user so
          // Issue pages show "Users (90d): 1+" instead of "0". The sentry.ts
          // contract is "no PII" — id only.
          if (newSession?.user?.id) {
            setSentryUser(newSession.user.id);
          }
        }
        if (event === "SIGNED_OUT") {
          // Clear Sentry user identity so post-signout errors aren't
          // attributed to the previous account.
          setSentryUser(null);
          // Record whether this signout was triggered by our own code
          // (via the markSignoutSelfInitiated flag set in lib/auth.ts and
          // lib/api.ts) or fired by the SDK from somewhere we didn't
          // initiate — the latter is the multi-tab refresh-rotation race
          // signature we're trying to catch on Safari mobile.
          const selfInitiated = isSignoutSelfInitiated();
          logAuthEvent('signed_out_event', {
            selfInitiated,
            url: window.location.pathname + window.location.search,
          });

          // ── Ghost sign-out guard ──────────────────────────────────
          // When SIGNED_OUT fires without our marker flag (selfInitiated
          // false), the source is one of: (a) auth-js internally calling
          // _removeSession after a transient refresh hiccup; (b) a
          // localStorage event echo from another tab; (c) a stray
          // SIGNED_OUT dispatch from somewhere in the library we can't
          // suppress with autoRefreshToken:false alone. Before accepting
          // the sign-out, ask the SDK whether the session is actually
          // gone. If it's still there with a non-expired access token,
          // the SIGNED_OUT was spurious and we ignore it — preserving
          // the user's state instead of clearing the query cache and
          // sending them to /login mid-session.
          //
          // Safe because: a real sign-out (user-clicked or our 401
          // handler) routes through `signOut()` in lib/auth.ts which
          // sets the marker flag → never reaches this guard. A real
          // refresh-token rejection clears the local session BEFORE
          // emitting SIGNED_OUT, so getSession will correctly return
          // null and we accept the sign-out as expected.
          if (!selfInitiated) {
            authClient.getSession().then(({ data }) => {
              const liveSess = data.session;
              const now = Math.floor(Date.now() / 1000);
              const stillValid = !!(
                liveSess?.access_token &&
                liveSess.expires_at &&
                liveSess.expires_at - now > 0
              );
              if (stillValid) {
                logAuthEvent('signed_out_ignored', {
                  reason: 'spurious — session still valid',
                  secondsLeft: liveSess.expires_at
                    ? liveSess.expires_at - now
                    : null,
                });
                // Re-hydrate React state from the live session so the
                // setSession(newSession) call above (which passed null
                // for this event) doesn't strand us in a no-session
                // render even though the SDK still has one.
                setSession(liveSess);
                return;
              }
              // Confirmed sign-out path. Mirror the destructive cleanup
              // the marked-self-initiated branch runs below.
              logAuthEvent('signed_out_confirmed', { reason: 'session gone after verify' });
              setSession(null);
              setIsPasswordRecovery(false);
              localStorage.removeItem("trybeup_session_temporary");
              localStorage.removeItem("trybeup_last_active");
              queryClient.clear();
              unregisterPush().catch(() => {});
              revenuecatLogOut().catch(() => {});
            }).catch(() => {
              // getSession threw while verifying a SPURIOUS (non-self-initiated)
              // SIGNED_OUT. Previously we tore the session down here — but a
              // transient durable-storage / IndexedDB read hiccup on a cold
              // WKWebView (the same class the no-op lock in lib/auth.ts guards)
              // would then turn a storage blip into a real logout. Prefer to
              // PRESERVE the session: this event wasn't initiated by us, and if
              // the session is genuinely dead the next API 401 routes through
              // the confirmed sign-out path anyway. Fixing an over-eager
              // teardown, 2026-08-08.
              logAuthEvent('signed_out_ignored', {
                reason: 'verify threw — preserving session (transient storage error)',
              });
            });
            return;
          }

          // Self-initiated sign-out path (user clicked sign-out, or
          // our 401 handler called signOut after a confirmed
          // refresh-token rejection). Trusted — clear state directly.
          setSession(null);
          setIsPasswordRecovery(false);
          localStorage.removeItem("trybeup_session_temporary");
          localStorage.removeItem("trybeup_last_active");
          // Clear every cached query so the next user on this device
          // (shared phone, family iPad, hand-off scenario) doesn't see
          // a flash of the previous user's habits/workouts/profile
          // while the staleTime ticks down. Mobile makes this scenario
          // common; on desktop it was already rare.
          queryClient.clear();
          // Unregister push so signed-out users stop receiving notifications.
          unregisterPush().catch(() => {});
          // Reset RevenueCat identity so any subsequent IAP on a shared
          // device gets filed against the next user, not the one just
          // signed out.
          revenuecatLogOut().catch(() => {});
        }
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // Heartbeat: while the page is visible, refresh the temp-session "last
  // active" timestamp every 30s and on visibility changes. The load-time
  // check above only signs the user out if this timestamp is older than the
  // idle limit, so as long as the tab keeps refreshing it (or Safari resumes
  // it within the limit) the session survives sleep cycles.
  useEffect(() => {
    const beat = () => {
      if (
        document.visibilityState === "visible" &&
        localStorage.getItem("trybeup_session_temporary") === "true"
      ) {
        localStorage.setItem("trybeup_last_active", String(Date.now()));
      }
    };
    beat();
    const id = window.setInterval(beat, 30_000);
    document.addEventListener("visibilitychange", beat);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", beat);
    };
  }, []);

  // Session warmer. GoTrue access tokens expire after 1 hour
  // (GOTRUE_JWT_EXP=3600; raised from 900/15 min on 2026-07-15 to cut
  // refresh-token rotation races — see docker-compose.prod.yml).
  // With autoRefreshToken disabled in lib/auth.ts,
  // a token can sit expired in localStorage while the user is away —
  // and the first API call after they return triggers a refresh that,
  // if it hits a deploy window / brief network blip / refresh-token
  // rotation race, silently signs the user out. ("Google keeps signing
  // me out on Safari mobile.") This effect proactively refreshes the
  // token BEFORE the user makes any API call: on mount, and whenever
  // the tab transitions from hidden → visible (which is exactly the
  // "I came back to the app after closing it" case). The reactive
  // refresh-on-401 in api.ts stays as a safety net for tokens that
  // expire mid-session.
  useEffect(() => {
    let cancelled = false;
    // Refresh when within 5 min of expiry. Raised from 90s (2026-08-08) so the
    // periodic timer below — which can only check every few minutes — always
    // catches the token before it lapses, instead of missing the 90s window
    // between ticks. Refreshing a bit early is harmless.
    const REFRESH_WHEN_SECONDS_LEFT = 300;

    const warmSession = async () => {
      try {
        const { data } = await authClient.getSession();
        const sess = data.session;
        if (!sess?.expires_at) {
          logAuthEvent('warmer_run', { skipped: true, reason: 'no session or no expires_at' });
          return;
        }
        const now = Math.floor(Date.now() / 1000);
        const secondsRemaining = sess.expires_at - now;
        if (secondsRemaining >= REFRESH_WHEN_SECONDS_LEFT) {
          logAuthEvent('warmer_run', { skipped: true, secondsRemaining });
          return;
        }

        logAuthEvent('warmer_run', { skipped: false, secondsRemaining });
        // tryRefreshSession now retries-once-after-1500ms internally
        // when the first attempt returns auth-failed, so the
        // multi-tab / multi-device refresh-token rotation race is
        // handled there. The warmer only needs to act on the final
        // outcome. (Previously the retry lived here in the warmer; it
        // was moved into tryRefreshSession on 2026-06-19 so the same
        // protection applies to the API 401 path — without that, a
        // ProfilePage open could fire 5+ parallel queries that all
        // shared a single failed refresh and sent the user straight to
        // /login. Founder report: "tap Identity Stage → long delay →
        // logged out".)
        const outcome = await tryRefreshSession();
        logAuthEvent('warmer_refresh_outcome', { outcome });
        if (cancelled) return;
        if (outcome === "ok") {
          const { data: refreshed } = await authClient.getSession();
          if (!cancelled) setSession(refreshed.session);
          return;
        }
        if (outcome === "network-failed") {
          // Keep the (possibly expired) session in place. Reactive
          // refresh on the next API 401 will retry.
          return;
        }
        // outcome === "auth-failed" after internal retry already failed
        // twice. Accept the sign-out. Log a dedicated event so the
        // /login diagnostic banner can surface this path (the
        // SDK-emitted SIGNED_OUT is separate and may or may not fire
        // depending on internal state).
        logAuthEvent('warmer_signout', { reason: 'refresh auth-failed (after internal retry)' });
        // Full teardown, not a bare setSession(null). The warmer's auth-failed
        // path used to just null React state, leaving the dead session in
        // durable storage AND the previous user's TanStack Query cache intact
        // (autoRefreshToken is off, so GoTrue emits no SIGNED_OUT here and the
        // ghost-guard cleanup never runs). On a shared device the next user
        // then booted with the prior user's cached /api/users/me + habits until
        // a refetch replaced it. Route through the signOut wrapper (marks
        // self-initiated → synchronous cleanup: queryClient.clear(), durable
        // session + temp flags cleared), mirroring the idle-temp-session path
        // above (audit 2026-07-19).
        signOut('warmer-auth-failed').catch(() => {});
        setSession(null);
      } catch {
        // Defensive — never let the warmer itself crash or affect the
        // existing auth flow.
      }
    };

    warmSession();
    const onVisibility = () => {
      if (document.visibilityState === "visible") warmSession();
    };
    document.addEventListener("visibilitychange", onVisibility);
    // Periodic warm while the app is FOREGROUNDED. The visibility-change trigger
    // above never fires during a long, uninterrupted session (app open, screen
    // on — e.g. a >1hr workout with a wake-lock), so with a 1hr token and
    // autoRefreshToken off the token silently expired mid-session and the next
    // request could bounce the user to /login (founder: logged-out screen with a
    // workout still running, 2026-08-07). This timer refreshes it in the
    // background before it lapses. Every 4 min, paired with the 5-min margin
    // above, guarantees a check inside the refresh window. iOS throttles/pauses
    // timers while backgrounded, so this only does work when foregrounded —
    // exactly the gap; the return-to-foreground case stays covered by
    // onVisibility.
    const warmInterval = window.setInterval(() => {
      if (document.visibilityState === "visible") warmSession();
    }, 4 * 60 * 1000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(warmInterval);
    };
  }, []);

  const {
    data: user,
    isLoading: userLoading,
    isError: userError,
    isFetching: userIsFetching,
    refetch: refetchUser,
  } = useQuery<User | null>({
    queryKey: ["/api/users/me"],
    enabled: !!session && !sessionLoading && !isPasswordRecovery,
    // This query GATES first paint (see isLoading below: the whole app
    // shows a spinner until it resolves). The old retry:3 with backoff
    // up to 5s/attempt meant one slow/flaky cold-start call could hang
    // the splash for ~7s (founder report 2026-06-13: 7s cold start).
    // Bound it: 2 retries, 600ms cap → worst-case boot retry cost ~1.2s
    // instead of ~7s. Genuine failures surface via isUserError (which
    // has its own retry UI) sooner, rather than freezing the splash.
    retry: 2,
    retryDelay: (attempt) => Math.min(300 * 2 ** attempt, 600),
    staleTime: 30_000,
    queryFn: async () => {
      try {
        // Per-request timeout (12s) so a STALLED connection (request sent,
        // no response, never rejects) can't freeze the splash forever — on
        // timeout axios rejects, the retry below fires, and a genuine failure
        // lands on the isUserError retry screen instead of an endless spinner.
        const { data } = await api.get("/api/users/me", { timeout: 12000 });
        return normalizeUser(data);
      } catch (err: unknown) {
        if (
          err &&
          typeof err === "object" &&
          "response" in err &&
          (err as { response?: { status?: number } }).response?.status === 403
        ) {
          // /api/users/me is exempt from the profile gate, so a gate 403 here
          // shouldn't be possible — but if that exemption ever regresses, an
          // incomplete account must NOT be shown the "Account Suspended" wall.
          const detail = (err as { response?: { data?: { detail?: { code?: string } } } })
            .response?.data?.detail;
          if (detail && typeof detail === "object" && detail.code === "profile_incomplete") {
            throw err;
          }
          setIsSuspended(true);
          return null;
        }
        throw err;
      }
    },
  });

  // Fresh-fetch tracker for the ProfileGate — see the userFetchedFresh note in
  // the return value. Flips true the first time an /api/users/me fetch this
  // session completes SUCCESSFULLY; cache hydration never sets isFetching, so a
  // restored stale user can't count as fresh either.
  //
  // The success requirement is the fix for the founder's 2026-08-10 report
  // ("updating to 1.2.50 and the name popup came back", with all four fields
  // already filled). The previous version deliberately counted an ERRORED
  // fetch as fresh, reasoning that a failed fetch shouldn't suppress the gate
  // forever. That reasoning is backwards for a BLOCKING form: when the fetch
  // fails we know nothing about the server's view of the profile, and the only
  // `user` left is the stale persisted copy — which, for an account that
  // predates filling these fields, still looks incomplete. A Capgo update
  // reloads the app mid-flight, so a dropped /api/users/me is exactly when
  // this happened. Showing an unskippable form off stale data is strictly
  // worse than showing nothing and letting the next successful fetch decide:
  // a genuinely incomplete account still gets gated the moment any fetch
  // succeeds, and the server keeps refusing its endpoints with 403 meanwhile,
  // so nothing is bypassed.
  const [userFetchedFresh, setUserFetchedFresh] = useState(false);
  const sawUserFetchRef = useRef(false);
  useEffect(() => {
    if (userIsFetching) {
      sawUserFetchRef.current = true;
      return;
    }
    // A completed fetch that ERRORED leaves us with stale data — not fresh
    // knowledge. Wait for one that actually returns the user.
    if (sawUserFetchRef.current && !userError && user !== undefined) {
      setUserFetchedFresh(true);
    }
  }, [userIsFetching, userError, user]);

  const gamificationData =
    user && !hasShownGamification.current && user.xpGained && user.xpGained > 0
      ? {
          xpGained: user.xpGained,
          leveledUp: user.leveledUp,
          newLevel: user.newLevel,
          streakBonus: user.streakBonus,
        }
      : null;

  useEffect(() => {
    if (gamificationData) {
      hasShownGamification.current = true;
    }
  }, [gamificationData]);

  // ── Auto-sync browser timezone to backend on first load per device ──
  // Every timezone-aware feature (weekly digest, daily meal limit, streaks)
  // uses `User.timezone`, which defaults to "America/New_York". We detect
  // the browser's actual IANA timezone and push it to the backend once per
  // device so each user's digests and day boundaries match their real
  // location. We respect manual overrides — if the stored timezone is
  // something non-default that differs from the browser, we assume the
  // user picked it intentionally and leave it alone.
  useEffect(() => {
    if (!user || !user.id) return;
    const syncKey = `trybeup_tz_synced_${user.id}`;
    try {
      if (localStorage.getItem(syncKey) === "true") return;
    } catch {
      return;
    }

    let browserTz: string | null = null;
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      return;
    }
    if (!browserTz) return;

    const storedTz = user.timezone;
    const DEFAULT_TZ = "America/New_York";

    // Respect an explicit non-default manual choice.
    if (storedTz && storedTz !== DEFAULT_TZ && storedTz !== browserTz) {
      try { localStorage.setItem(syncKey, "true"); } catch {}
      return;
    }
    // Already matches — nothing to do.
    if (storedTz === browserTz) {
      try { localStorage.setItem(syncKey, "true"); } catch {}
      return;
    }

    // Push browser timezone to backend.
    api
      .patch("/api/users/me", { timezone: browserTz })
      .then(() => {
        try { localStorage.setItem(syncKey, "true"); } catch {}
        refetchUser();
      })
      .catch(() => {
        // Silent fail — will retry next session.
      });
  }, [user?.id, user?.timezone, refetchUser]);

  // ── Daily login-XP claim ──────────────────────────────────────────
  // The backend's `login_streak` (and the Identity Stage progression
  // built on top of it) only advances when POST /api/users/me/login-xp
  // is called once per local day. The endpoint is idempotent — it
  // checks `last_xp_login_date` against the user's timezone-aware
  // `today` and returns `already_claimed: true` on repeats — so it's
  // safe to fire every time we have a logged-in user. We still gate
  // with a per-user-per-day localStorage flag so we don't hit a
  // strictly-rate-limited endpoint on every page navigation.
  useEffect(() => {
    if (!user || !user.id) return;
    const todayKey = new Date().toISOString().slice(0, 10);
    const storageKey = `trybeup_login_claim_${user.id}`;
    try {
      if (localStorage.getItem(storageKey) === todayKey) return;
    } catch {
      // localStorage blocked (Safari private mode, etc.) — fall through
      // and call the endpoint; backend will dedupe.
    }
    api
      .post("/api/users/me/login-xp")
      .then(() => {
        try { localStorage.setItem(storageKey, todayKey); } catch {}
        refetchUser();
      })
      .catch(() => {
        // Silent — will retry on next mount. login_streak / xp updates
        // are non-critical; a transient network blip shouldn't surface.
      });
  }, [user?.id, refetchUser]);

  // ── PostHog identity sync ─────────────────────────────────────────
  // Attach a PostHog person profile to the browser session the moment
  // we have a confirmed user. On logout / session end we reset so the
  // next user doesn't inherit the previous one's identity. No-op when
  // VITE_POSTHOG_KEY isn't set.
  useEffect(() => {
    Promise.all([
      import("@/lib/posthog"),
      import("@/lib/sentry"),
    ]).then(([{ identifyUser, resetPostHog }, { setSentryUser }]) => {
      if (session && user?.id) {
        identifyUser(user.id, {
          email: user.email ?? undefined,
          first_name: user.firstName ?? undefined,
          is_premium: (user as unknown as { isPremium?: boolean; is_premium?: boolean }).isPremium
            ?? (user as unknown as { is_premium?: boolean }).is_premium
            ?? false,
          is_admin: (user as unknown as { isAdmin?: boolean; is_admin?: boolean }).isAdmin
            ?? (user as unknown as { is_admin?: boolean }).is_admin
            ?? false,
          created_at: user.createdAt ?? undefined,
        });
        // Set user id on Sentry so errors can be traced across the stack.
        // id only — no PII. Mirrors the backend's set_user({"id": ...}).
        setSentryUser(String(user.id));
      } else if (!session) {
        resetPostHog();
        setSentryUser(null);
      }
    }).catch(() => { /* silent — analytics must never break the app */ });
  }, [session, user?.id, user?.email, user?.firstName, user?.createdAt]);

  // Splash watchdog: getSession() normally settles in <1s, flipping
  // sessionLoading false. In the pathological case where it never resolves
  // OR rejects (a hung GoTrue storage/lock read), the spinner would otherwise
  // stay up forever. After 15s of still-loading, force it false so the app
  // falls through to the unauth/Gateway path (a clear screen the user can act
  // on) instead of an endless spinner. Only fires in the degenerate case —
  // the cleanup clears the timer the instant sessionLoading flips normally.
  useEffect(() => {
    if (!sessionLoading) return;
    const t = setTimeout(() => {
      logAuthEvent("session_loading_watchdog", { reason: "forced sessionLoading=false after 15s" });
      setSessionLoading(false);
    }, 15000);
    return () => clearTimeout(t);
  }, [sessionLoading]);

  const isLoading = sessionLoading || (!!session && !isPasswordRecovery && userLoading);

  // Boot-timing instrumentation — mark when the session resolves and
  // when the overall auth gate clears (first interactive paint), then
  // fire the one-shot PostHog `boot_timing` event. Lets us see the real
  // cold-start breakdown instead of guessing (founder 7s report
  // 2026-06-13). See lib/bootTiming.ts. flushBootTiming is one-shot, so
  // these effects firing on every render is harmless.
  useEffect(() => {
    if (!sessionLoading) markBoot('session_ready');
  }, [sessionLoading]);
  useEffect(() => {
    if (!isLoading) {
      markBoot('app_ready');
      flushBootTiming();
    }
  }, [isLoading]);

  return {
    session,
    user: user ?? null,
    isLoading,
    loading: isLoading,
    isAuthenticated: !!session,
    isPasswordRecovery,
    isSuspended,
    isUserError: userError && !isSuspended,
    // True once /api/users/me has been fetched fresh THIS session (not merely
    // restored from the persisted query cache). The ProfileGate keys on this so
    // a stale restored user can't flash the "One quick thing" popup before the
    // real server state loads (founder saw it recur; 2026-08-08).
    //
    // NOT isFetchedAfterMount (adversarial review 2026-08-09): that flag is
    // `dataUpdateCount > initial`, and PersistQueryClientProvider hydrates the
    // PERSISTED dataUpdateCount (>0) onto a query whose observer mounted with 0
    // during first render — so a pure cache restore flipped it true with no
    // network involved, which is exactly the case it was meant to exclude.
    // This tracker instead watches isFetching: hydration never sets isFetching,
    // only a real fetch does, so fresh means "a network round-trip completed
    // this JS session AND returned the user". A failed fetch no longer counts
    // (2026-08-10) — see the tracker for why an errored fetch must not open a
    // blocking form off stale data.
    userFetchedFresh,
    gamificationData,
    refetchUser,
  };
}
